import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { getEnecoBeXapiSiteApiV1OrdersByOrderId } from '@eneco/dc-be/client/services.gen';
import type { GetOrderResponse } from '@eneco/dc-be/client/types.gen';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useRouter } from '@eneco/next';
import { Box, NotificationBox, PageGrid, Stack } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { isSameMonth } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import { useQueryParam } from 'use-query-params';

import { FlowNavigationContext } from '../../../providers/FlowNavigationProvider';
import { buildNavigationHref } from '../../../utils/buildNavigationHref';
import { priceSimulationSteps } from '../../price-simulation/types/PriceSimulationSteps';
import { ProductOverviewInSidebar } from '../components/ProductOverviewInSidebar';
import { OrderManagementContext } from '../contexts/OrderManagementProvider';
import { useOrderId } from '../hooks/useOrderId';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { BoilerMaintenanceStep } from '../steps/BoilerMaintenanceStep';
import { DeliveryAddressStep } from '../steps/DeliveryAddressStep';
import { ElectricityDetailsStep } from '../steps/ElectricityDetailsStep';
import { GasDetailsStep } from '../steps/GasDetailsStep';
import { MyEnecoAccount } from '../steps/MyEnecoAccount';
import { OrderConfirmationStep } from '../steps/OrderConfirmationStep';
import { OverviewStep } from '../steps/OverviewStep';
import { PersonalDetailsStep } from '../steps/PersonalDetailsStep';
import { SelectedProductsOverview } from '../steps/selectedProductsOverview/SelectedProductsOverview';
import { SelectPaymentMethodStep } from '../steps/SelectPaymentMethodStep';
import { ServiceProposalStep } from '../steps/ServiceProposalStep';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { OrderManagementStep, activeHeaderIndexForStep, buildDynamicStepOrder } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { loadOrderManagementState, saveOrderManagementState } from '../utils/orderManagementStorage';

export const OrderManagementStepperFlow = () => {
  const {
    subscribeTriggerOnPreviousClick,
    subscribeTriggerOnCloseClick,
    setActiveStepIndicator,
    setShowPreviousBtn,
    activeStepIndicator,
  } = useContext(FlowNavigationContext);
  const { fields } = useContent<OrderManagementWithItems>();
  const { trackEventBE } = useTracking();
  const { push, query, pathname } = useRouter();
  const { setIsLoadingInitialOrder } = useContext(OrderManagementContext);
  const { data: session } = useSession();

  const priceSimulationId = query.priceSimulationId as string | undefined;
  const orderId = useOrderId();

  const orderIdForFetch = (query.orderId as string | undefined) || loadOrderManagementState().orderId;
  const { data: orderFetchRaw } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    {
      path: {
        orderId: orderIdForFetch ?? '',
      },
    },
    [`/orders/${orderIdForFetch}`],
    !!orderIdForFetch,
    undefined,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const orderFromApi = unwrapData(orderFetchRaw) as GetOrderResponse | null;
  const { watch, setValue, getValues } = useFormContext<OrderManagementFormSchema>();

  const serviceProposal = watch('serviceProposal');
  const boilerMaintenance = watch('boilerMaintenance');

  const [orderManagementStep, setOrderManagementStep] = useState<OrderManagementStep | null>(null);

  const { hasSelectedMaintenance, hasSelectedElectricityProduct, hasSelectedGasProduct, hasSelectedServices } =
    useSelectedProducts({ orderId });

  useEffect(() => {
    setValue('hasSelectedGasProduct', hasSelectedGasProduct, { shouldValidate: false });
  }, [hasSelectedGasProduct, setValue]);

  const isLoggedIn = session !== null;
  const swapOrder = query['swapOrder'] as string;
  const dynamicStepOrder = useMemo(
    () =>
      buildDynamicStepOrder({
        hasSelectedMaintenance,
        hasSelectedServices,
        hasSelectedElectricityProduct,
        hasSelectedGasProduct,
        isLoggedIn,
        swapOrder: swapOrder === 'true',
      }),
    [
      hasSelectedElectricityProduct,
      hasSelectedGasProduct,
      hasSelectedMaintenance,
      hasSelectedServices,
      isLoggedIn,
      swapOrder,
    ],
  );
  const [boilerMaintenanceQueryParam, setBoilerMaintenanceQueryParam] = useQueryParam<string | null>(
    'boilerMaintenance',
  );
  const [serviceProposalQueryParam, setServiceProposalQueryParam] = useQueryParam<string | null>('serviceProposal');

  // On mount/refresh, sync query params back into the form
  useEffect(() => {
    if (boilerMaintenanceQueryParam) {
      setValue('boilerMaintenance', boilerMaintenanceQueryParam);
    }
    if (serviceProposalQueryParam) {
      setValue('serviceProposal', serviceProposalQueryParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const boilerValue = boilerMaintenance && boilerMaintenance !== 'none' ? boilerMaintenance : null;
    if (boilerMaintenanceQueryParam !== boilerValue) {
      setBoilerMaintenanceQueryParam(boilerValue, 'replaceIn');
    }

    const serviceValue = serviceProposal && serviceProposal !== 'none' ? serviceProposal : null;
    if (serviceProposalQueryParam !== serviceValue) {
      setServiceProposalQueryParam(serviceValue, 'replaceIn');
    }
  }, [
    boilerMaintenance,
    serviceProposal,
    boilerMaintenanceQueryParam,
    serviceProposalQueryParam,
    setBoilerMaintenanceQueryParam,
    setServiceProposalQueryParam,
  ]);

  // Helper function to update step with all side effects
  type UpdateStepType = { newStep?: OrderManagementStep; setHash?: boolean; orderId?: string };

  const updateStep = useCallback(
    (updateStepType: UpdateStepType) => {
      const { newStep, setHash = true, orderId: stepOrderId } = updateStepType;

      const targetStep = newStep || dynamicStepOrder[0];
      setOrderManagementStep(targetStep);

      if (setHash) {
        if (stepOrderId) {
          saveOrderManagementState({ orderId: stepOrderId });
          void push(
            {
              pathname,
              query: { ...query, orderId: stepOrderId },
              hash: targetStep,
            },
            undefined,
            { shallow: true, scroll: false },
          );
        } else {
          const { pathname, search } = window.location;
          window.history.replaceState(window.history.state, '', `${pathname}${search}#${targetStep}`);
        }
      }

      setActiveStepIndicator(activeHeaderIndexForStep.find(step => step.step === targetStep)?.activeHeaderIndex ?? 0);
    },
    [dynamicStepOrder, setActiveStepIndicator, push, pathname, query],
  );

  const previousMainStepLogicManager = useCallback(() => {
    const previousStepIndex = dynamicStepOrder.indexOf(orderManagementStep ?? 'boilerMaintenanceStep') - 1;
    if (previousStepIndex >= 0) {
      updateStep({ newStep: dynamicStepOrder[previousStepIndex] });
    } else {
      push(
        buildNavigationHref(fields.navigation.priceSimulationLink.value.href, {
          queryParams: {
            priceSimulationId,
            orderId,
          },
          stepHash: priceSimulationSteps.priceSimulationResult,
        }),
      );
    }
  }, [
    orderManagementStep,
    updateStep,
    dynamicStepOrder,
    push,
    fields.navigation.priceSimulationLink.value.href,
    priceSimulationId,
    orderId,
  ]);

  const previousMainStep = useCallback(
    (transition?: OrderManagementStep) => {
      if (transition) updateStep({ newStep: transition });
      else previousMainStepLogicManager();
    },
    [previousMainStepLogicManager, updateStep],
  );

  const nextMainStepLogicManager = useCallback(
    (orderId?: string) => {
      const nextStepIndex = dynamicStepOrder.indexOf(orderManagementStep ?? 'boilerMaintenanceStep') + 1;
      if (nextStepIndex < dynamicStepOrder.length) {
        updateStep({ newStep: dynamicStepOrder[nextStepIndex], orderId });
      } else {
        throw new Error('No next step found');
      }
    },
    [dynamicStepOrder, orderManagementStep, updateStep],
  );

  const nextMainStep = useCallback(
    (transition?: OrderManagementStep, orderId?: string) => {
      if (transition) {
        updateStep({ newStep: transition, orderId });
      } else {
        nextMainStepLogicManager(orderId);
      }
    },
    [nextMainStepLogicManager, updateStep],
  );

  useEffect(() => {
    setIsLoadingInitialOrder(false);
  }, [orderManagementStep, setIsLoadingInitialOrder]);

  useEffect(() => {
    return subscribeTriggerOnPreviousClick(previousMainStep);
  }, [subscribeTriggerOnPreviousClick, previousMainStep]);

  // Useeffect for tracking analytics - close and previous clicks
  useEffect(() => {
    const unsubscribeClose = subscribeTriggerOnCloseClick(() => {
      trackEventBE('be_om_step_close', {
        data: { ...getTrackableFormValues(getValues()), orderManagementStep },
      });
    });

    const unsubscribePrevious = subscribeTriggerOnPreviousClick(() => {
      trackEventBE('be_om_step_back', {
        data: { ...getTrackableFormValues(getValues()), orderManagementStep },
      });
    });

    return () => {
      unsubscribeClose();
      unsubscribePrevious();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeTriggerOnCloseClick, subscribeTriggerOnPreviousClick]);

  useEffect(() => {
    const isFirstStep = dynamicStepOrder.indexOf(orderManagementStep ?? 'boilerMaintenanceStep') === 0;
    const isConfirmationStep = orderManagementStep === 'orderConfirmationStep';
    setShowPreviousBtn((!!priceSimulationId || !isFirstStep) && !isConfirmationStep);
  }, [setShowPreviousBtn, priceSimulationId, orderManagementStep, dynamicStepOrder]);

  // Set step from URL hash (finalized orders always stay on confirmation)
  useEffect(() => {
    const handleHashChange = () => {
      if (orderFromApi?.finalized) {
        if (orderManagementStep !== 'orderConfirmationStep') {
          updateStep({
            newStep: 'orderConfirmationStep',
            orderId: orderFromApi.orderId,
          });
        }
        return;
      }

      const hash = window.location.hash;
      const urlStep = hash.slice(1) as OrderManagementStep;

      // Don't update if we're already on this step
      if (orderManagementStep === urlStep) {
        return;
      } else updateStep({ newStep: urlStep, setHash: true });
    };

    handleHashChange();
    // Listen for hash changes (browser navigation)
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [dynamicStepOrder, orderFromApi, orderManagementStep, updateStep]);

  const showSidebar = activeStepIndicator === 2 || activeStepIndicator === 3;
  const gridColumnDesktop = activeHeaderIndexForStep.find(step => step.step === orderManagementStep)?.gridColumnDesktop;

  return (
    <PageGrid>
      <PageGrid.Item
        gridColumn={{
          initial: '-1/1',
          lg: gridColumnDesktop,
        }}>
        <Box paddingY="12">
          <Stack gap="10" alignX={'start'}>
            {showSidebar ? (
              <ProductOverviewInSidebar>
                <NewTariffNotification />
                <StepContent step={orderManagementStep} nextMainStep={nextMainStep} />
              </ProductOverviewInSidebar>
            ) : (
              <>
                <NewTariffNotification />
                <StepContent step={orderManagementStep} nextMainStep={nextMainStep} />
              </>
            )}
          </Stack>
        </Box>
      </PageGrid.Item>
    </PageGrid>
  );
};

const NewTariffNotification = () => {
  const { searchParams } = useApplication();
  const orderId = searchParams.get('orderId') as string | undefined;
  const { fields } = useContent<OrderManagementWithItems>();
  const { data } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    {
      path: {
        orderId: orderId ?? '',
      },
    },
    [`/orders/${orderId}`],
    !!orderId,
  );
  const orderData = unwrapData(data) as GetOrderResponse | null;
  const hasReminderEmailSent = !!orderData?.reminderEmailSentAt;
  if (!hasReminderEmailSent) {
    return null;
  }
  const hasNewTariff = !isSameMonth(new Date(orderData.reminderEmailSentAt!), new Date());
  if (!hasNewTariff) {
    return null;
  }
  return (
    <NotificationBox
      isAlert={false}
      variant={fields.navigation.newTariffNotification.value.variant}
      title={fields.navigation.newTariffNotification.value.title}
      text={<RichText html={fields.navigation.newTariffNotification.value.content} />}
    />
  );
};

const StepContent = ({
  step,
  nextMainStep,
}: {
  step: OrderManagementStep | null;
  nextMainStep: (transition?: OrderManagementStep, orderId?: string) => void;
}) => {
  const previousStepRef = useRef<OrderManagementStep | null>(null);

  useLayoutEffect(() => {
    if (!step) {
      return;
    }

    const previousStep = previousStepRef.current;
    previousStepRef.current = step;

    if (previousStep !== null && previousStep !== step) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [step]);

  switch (step) {
    case 'boilerMaintenanceStep':
      return <BoilerMaintenanceStep nextMainStep={nextMainStep} />;
    case 'serviceProposalStep':
      return <ServiceProposalStep nextMainStep={nextMainStep} />;
    case 'selectedProductsOverviewStep':
      return <SelectedProductsOverview nextMainStep={nextMainStep} />;
    case 'myEnecoAccountStep':
      return <MyEnecoAccount nextMainStep={nextMainStep} />;
    case 'personalDetailsStep':
      return <PersonalDetailsStep nextMainStep={nextMainStep} />;
    case 'deliveryAddressStep':
      return <DeliveryAddressStep nextMainStep={nextMainStep} />;
    case 'electricityDetailsStep':
      return <ElectricityDetailsStep nextMainStep={nextMainStep} />;
    case 'gasDetailsStep':
      return <GasDetailsStep nextMainStep={nextMainStep} />;
    case 'selectPaymentMethodStep':
      return <SelectPaymentMethodStep nextMainStep={nextMainStep} />;
    case 'overviewStep':
      return <OverviewStep nextMainStep={nextMainStep} />;
    case 'orderConfirmationStep':
      return <OrderConfirmationStep nextMainStep={nextMainStep} />;
    default:
      return null;
  }
};
